import classNames from 'classnames/bind';
import PropTypes from 'prop-types';
import React from 'react';
import storeJS from 'store';

import Button from 'uikit/Button';
import CleanedText from 'uikit/CleanedText';
import Icon from 'uikit/Icon';

import styles from './ClassificationMatrix.module.scss';

const cx = classNames.bind(styles);

const ClassificationMatrix = ({
  className,
  course,
  handlePlotInit,
  handleSectionToggle,
  isSectionOpen,
  selection,
  t,
}) => (
  <div
    aria-label={t(
      'Course/Teacher/Statistics/ClassificationMatrix/aria-matrix-description',
    )}
    className={cx('container', className, { isSectionOpen })}
  >
    <button
      className={cx('header')}
      onClick={handleSectionToggle}
      type="button"
    >
      <div className={cx('title-container')}>
        <Icon className={cx('title-icon')} name="chart-mixed" />
        <h2 className={cx('title')}>
          {t('Course/Teacher/Statistics/ClassificationMatrix/title')}
        </h2>
      </div>
      <div className={cx('header-right')}>
        <Button
          as="a"
          className={cx('download-button')}
          href={`${
            process.env.REACT_APP_API_URL
          }/rest/study-stats/excel?courseId=${
            course.id
          }&authToken=${storeJS.get('token')}`}
          target="blank"
        >
          <Icon className={cx('download-button-icon')} name="download" />
          <span className={cx('download-button-text')}>
            {t('Course/Teacher/Statistics/ClassificationMatrix/download')}
          </span>
        </Button>
        <Icon
          className={cx('toggle-button')}
          name={isSectionOpen ? 'chevron-up' : 'chevron-down'}
        />
      </div>
    </button>
    {isSectionOpen && (
      <div className={cx('chart-container')}>
        <div className={cx('chart')}>
          <svg ref={handlePlotInit} />
        </div>
        <div
          className={cx('tooltip', {
            isVisible: !!selection,
          })}
        >
          {selection && (
            <div>
              <p className={cx('student')}>{selection.student}</p>
              <CleanedText className={cx('question')}>
                {selection.question}
              </CleanedText>
              <p className={cx('stat', 'total')}>
                <span>{selection.trials}</span>
                {t('Course/Teacher/Statistics/ClassificationMatrix/trials', {
                  count: selection.trials,
                })}
              </p>
              <p className={cx('stat', 'correct')}>
                <span>{selection.correct}</span>
                {t('Course/Teacher/Statistics/ClassificationMatrix/correct', {
                  count: selection.correct,
                })}
              </p>
              <p className={cx('stat', 'wrong')}>
                <span>{selection.wrong}</span>
                {t('Course/Teacher/Statistics/ClassificationMatrix/wrong', {
                  count: selection.wrong,
                })}
              </p>
              <p className={cx('stat', 'unknown')}>
                <span>{selection.unknown}</span>
                {t('Course/Teacher/Statistics/ClassificationMatrix/unknown', {
                  count: selection.unknown,
                })}
              </p>
              <p className={cx('history-title')}>
                {t('Course/Teacher/Statistics/ClassificationMatrix/history')}:
              </p>
              <p className={cx('history')}>
                {selection.history.map((answer, i) =>
                  answer === 'correct' ? (
                    <Icon
                      className={cx('icon', 'correct')}
                      // eslint-disable-next-line react/no-array-index-key
                      key={i}
                      name="check"
                    />
                  ) : answer === 'wrong' ? (
                    <Icon
                      className={cx('icon', 'wrong')}
                      // eslint-disable-next-line react/no-array-index-key
                      key={i}
                      name="times"
                    />
                  ) : answer === 'unknown' ? (
                    <Icon
                      className={cx('icon', 'unknown')}
                      // eslint-disable-next-line react/no-array-index-key
                      key={i}
                      name="question"
                    />
                  ) : null,
                )}
              </p>
            </div>
          )}
        </div>
      </div>
    )}
  </div>
);

ClassificationMatrix.propTypes = {
  className: PropTypes.string,
  course: PropTypes.object.isRequired,
  handlePlotInit: PropTypes.func.isRequired,
  handleSectionToggle: PropTypes.func.isRequired,
  isSectionOpen: PropTypes.bool.isRequired,
  selection: PropTypes.object,
  t: PropTypes.func.isRequired,
};

ClassificationMatrix.defaultProps = {
  className: undefined,
  selection: undefined,
};

export default ClassificationMatrix;
